import { useRef, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { IFormData, ITask } from "@/api/tasks/types";
import { IProjectsData, ISection } from "@/api/projects/types";
import { useUpdateTask } from "@/features/projects/hooks/tasks/useUpdateTask";
import { useGetAllTasksByProject } from "@/features/projects/hooks/tasks/useGetAllTasksByProject";
import { useGetAllProjects } from "@/features/projects/hooks/useGetAllProjects";
import { useProjectSections } from "@/features/projects/hooks/useProjectSections";
import { useAssignedUsers } from "@/features/projects/hooks/useAssignedUsers";
import { useGetAllUsers } from "@/features/users/hooks/useGetAllUsers";
import { useCreateAssignment } from "@/features/assignments/hooks/useCreateAssignment";
import { useDeleteAssignment } from "@/features/assignments/hooks/useDeleteAssignment";
import { useLoadingNotification } from "@/hooks/useLoadingNotification";
import { handleAxiosError } from "@/api/common/handleAxiosError";
import { formatDateCreateTask, formatCreatedAt } from "@/utils/dateFunctions";
import { IUsersData } from "@/api/users/types";
import { User } from "@/components/UI/UserAssigneeSelector/UserAssigneeSelector";
import showDetailProjectStyles from "@/features/projects/styles/showdetailproject.module.scss";
import ProjectsSelect from "../../ProjectsSelect";
import SectionsSelect from "../../SectionsSelect";
import { useAuth } from "@/features/authentication/hooks/useAuth";
import { useCreateComment } from "@/features/projects/hooks/comments/useCreateComment";
import { useGetAllCommentsByTask } from "@/features/projects/hooks/comments/useGetAllCommentsByTask";
import { ICreateCommentFormData } from "@/api/comments/types";
import { UserAssigneeSelector } from "@/components/UI/UserAssigneeSelector/UserAssigneeSelector";
import { TaskComments } from "@/features/projects/components/projectPage/components/Task/TaskComments";
import { useUploadTaskFile } from "@/features/projects/hooks/tasks/useUploadTaskFile";
import { useGetAllDocsByTask } from "@/features/projects/hooks/tasks/useGetAllDocsByTask";
import { useDownloadDocFromTask } from '@/features/projects/hooks/tasks/useDownloadDocFromTask';
import { useUploadDocToComment } from "@/features/projects/hooks/comments/useUploadDocToComment";
import { useGetAllDocsByComment } from "@/features/projects/hooks/comments/useGetAllDocsByComment";
import { useDownloadDocFromComment } from "@/features/projects/hooks/comments/useDownloadDocFromComment";
import { IDocUploaded } from "@/api/comments/types";

// Icons
import checkIcon from '@/features/projects/assets/icon_check-active.svg';
import projectIcon from '@/features/projects/assets/icon_project.svg';
import { CKEditorShared } from '@/components/ckeditor/CKEditorShared';
import { DateRangePicker, RangeKeyDict } from 'react-date-range';
import UIcon from '@/assets/uicons/UIcon';

interface IShowDetailProjectProps {
  closeDetailProject: () => void;
  showTask: ITask;
  idproject: string;
  projectName: string;
  createTask: (form: IFormData) => Promise<ITask>;
  onTaskUpdate?: () => void;
}

interface PreviewModalState {
  isOpen: boolean;
  file: File | { id: string; fileName: string; fileType: string } | null;
  url: string | null;
  contentType: string | null;
}

export const ShowTaskDetail = ({
  closeDetailProject,
  showTask: task,
  idproject,
  projectName,
  createTask,
  onTaskUpdate
}: IShowDetailProjectProps) => {
  const { isPending: isUpdatingTask, updateTaskHook } = useUpdateTask();
  const [description, setDescription] = useState(task.description || '');
  const [taskName, setTaskName] = useState(task.title || '');
  const [taskState, setTaskState] = useState(task.state);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(!!task.isBeingCreated);

  const [projects, setProjects] = useState<IProjectsData[]>([]);
  const [selectedProject, setSelectedProject] = useState<IProjectsData | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string>(task.projectId);
  
  // Estados para archivos adjuntos
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [previewModal, setPreviewModal] = useState<PreviewModalState>({
    isOpen: false,
    file: null,
    url: null,
    contentType: null
  });
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState<{
    isOpen: boolean;
    fileId: string | null;
  }>({
    isOpen: false,
    fileId: null
  });
  
  // Hooks para archivos
  const { mutateAsync: uploadFile } = useUploadTaskFile();
  const { docs: taskFiles, refetch: refetchTaskFiles } = useGetAllDocsByTask(task.id);
  const { mutateAsync: downloadDoc } = useDownloadDocFromTask();

  // Estados para comentarios con adjuntos
  const [isUploadingCommentDoc, setIsUploadingCommentDoc] = useState(false);
  const { uploadDoc: uploadCommentDoc } = useUploadDocToComment();
  const { docs: commentDocs, refetch: refetchCommentDocs } = useGetAllDocsByComment([task.id]);
  const { mutateAsync: downloadCommentDoc } = useDownloadDocFromComment();

  // Cerrar dropdown cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.file-thumbnail-dropdown-btn') && !target.closest('.file-dropdown-menu')) {
        closeDropdown();
      }
    };

    if (dropdownOpen.isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen.isOpen]);
  
  const titleInputRef = useRef<HTMLInputElement>(null);
  const [dateRange, setDateRange] = useState({
    startDate: task.startDate ? new Date(task.startDate) : new Date(),
    endDate: task.endDate ? new Date(task.endDate) : new Date(),
    key: 'selection',
  });

  // Resetear archivos adjuntos cuando cambie la tarea
  useEffect(() => {
    setAttachedFiles([]);
  }, [task.id]);

  const {
    setValue,
    trigger,
  } = useForm<IFormData>({
    defaultValues: {
      projectId: task.projectId || "",
      sectionId: task.sectionId || "",
      title: task.title || "",
      description: task.description || "",
      state: task.state || "incompleta",
      startDate: task.startDate || formatDateCreateTask(dateRange.startDate),
      endDate: task.endDate || formatDateCreateTask(dateRange.endDate),
      createdAt: task.createdAt || formatCreatedAt(new Date()),
      parentTaskId: task.parentTaskId || "",
    },
  });
  
  const { refetch: refetchTasks } = useGetAllTasksByProject({
    enabled: !!idproject,
    projectId: idproject,
  });

  // Obtener todos los proyectos
  const { data: projectsData } = useGetAllProjects();
  
  // Obtener y manejar secciones
  const { sections, selectedSection, setSelectedSection, updateSections } = useProjectSections(selectedProjectId, task.sectionId);

  // Actualizar la lista de proyectos cuando se obtengan los datos
  useEffect(() => {
    if (projectsData?.data) {
      setProjects(projectsData.data);
      // Establecer el proyecto seleccionado
      const currentProject = projectsData.data.find((p: IProjectsData) => String(p.id) === selectedProjectId);
      if (currentProject) {
        setSelectedProject(currentProject);
      }
    }
  }, [projectsData, selectedProjectId]);

  // Función wrapper para manejar la actualización de secciones
  const handleSetSections = (newSections: ISection[]) => {
    if (newSections.length > 0) {
      setSelectedSection(newSections[0]);
    } else {
      setSelectedSection(null);
    }
  };

  const { userAssigned, isLoading, refetchTaskAssignments } = useAssignedUsers({ taskId: task.id });
  useLoadingNotification({ validator: isLoading, message: "Cargando asignaciones..." });

  // Obtener usuarios activos
  const { data: usersData, isLoading: isLoadingUsers } = useGetAllUsers(1);
  const users = usersData?.data || [];

  // Mutaciones para asignar/desasignar
  const { mutateAsync: createAssignment } = useCreateAssignment();
  const { mutateAsync: deleteAssignment } = useDeleteAssignment();

  // Adaptar usuarios para el selector
  const userOptions: User[] = users.map((u: IUsersData) => ({
    id: String(u.userid),
    name: `${u.firstname} ${u.firstlastname}`.trim(),
    avatarUrl: undefined // o u.picture si existe
  }));

  // Usuario asignado (solo uno)
  const assignedUser: User | null = userAssigned && userAssigned.length > 0 ? {
    id: String(userAssigned[0].userid),
    name: `${userAssigned[0].firstname} ${userAssigned[0].firstlastname}`.trim(),
    avatarUrl: undefined // o userAssigned[0].picture si existe
  } : null;

  // Asignar usuario
  const handleAssign = async (user: User) => {
    const selected = users.find((u: IUsersData) => String(u.userid) === user.id);
    if (!selected) return;
    const formData = {
      taskId: task.id,
      taskTitle: task.title,
      username: selected.username,
      fullName: `${selected.firstname} ${selected.firstlastname} ${selected.secondlastname}`.trim(),
      startDate: task.startDate || null,
      endDate: task.endDate || null,
    };
    try {
      await createAssignment(formData);
      refetchTaskAssignments();
    } catch {
      // Manejo de error opcional
    }
  };

  // Desasignar usuario
  const handleUnassign = async (user: User) => {
    const assigned = userAssigned.find(u => String(u.userid) === user.id);
    if (!assigned) return;
    try {
      await deleteAssignment(assigned.assignmentId);
      refetchTaskAssignments();
    } catch {
      // Manejo de error opcional
    }
  };

  useEffect(() => {
    if (task.isBeingCreated && titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, [task.isBeingCreated]);



  useEffect(() => {
    setDescription(task.description || '');
    setTaskName(task.title || '');
    setTaskState(task.state);
    setDateRange({
      startDate: task.startDate ? new Date(task.startDate) : new Date(),
      endDate: task.endDate ? new Date(task.endDate) : new Date(),
      key: 'selection',
    });
  }, [task.id, task.description, task.title, task.state, task.startDate, task.endDate]);

  const onChangeDescription = (newDescription: string) => {
    setDescription(newDescription);
  };

  const saveTaskDescription = async () => {
    if (description === task.description) {
      setIsEditingDescription(false);
      return;
    }

    try {
      const updatedTask = {
        ...task,
        description
      };

      await toast.promise(
        updateTaskHook({
          taskId: task.id,
          task: updatedTask
        }, {
          onSuccess: () => {
            refetchTasks();
            setIsEditingDescription(false);
          }
        }),
        {
          pending: "Actualizando descripción...",
          success: "Descripción actualizada exitosamente",
          error: "Error al actualizar la descripción"
        }
      );
    } catch (error) {
      handleAxiosError(error, "Error al actualizar la descripción");
      setDescription(task.description || '');
    }
  };

  const cancelEditDescription = () => {
    setDescription(task.description || '');
    setIsEditingDescription(false);
  };

  const startEditingDescription = () => {
    setIsEditingDescription(true);
  };

  const handleTaskNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTaskName(e.target.value);
  };

  const saveTaskName = async () => {
    if (taskName === task.title || !taskName.trim()) {
      setTaskName(task.title || '');
      return;
    }

    if (task.isBeingCreated) {
      const formData: IFormData = {
        title: taskName.trim(),
        sectionId: task.sectionId,
        projectId: task.projectId,
        description: description,
        startDate: dateRange.startDate.toISOString(),
        endDate: dateRange.endDate.toISOString(),
      };
      await createTask(formData);
      closeDetailProject();
      return;
    }

    try {
      const updatedTask = {
        ...task,
        title: taskName.trim()
      };

      await updateTaskHook({
        taskId: task.id,
        task: updatedTask
      }, {
        onSuccess: () => {
          refetchTasks();
        }
      });
    } catch (error) {
      handleAxiosError(error, "Error al actualizar el título");
      setTaskName(task.title || '');
    }
  };

  const changeTaskStatus = async () => {
    try {
      const newState = taskState === 'incompleta' || taskState === "pendiente" ? 'completada' : 'incompleta';
      const updatedTaskData = { ...task, state: newState };

      const response = await updateTaskHook({
        taskId: task.id,
        task: updatedTaskData
      });

      if (response) {
        setTaskState(response?.data?.state);
        refetchTasks();
      }
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const getStatusText = () => {
    return taskState === 'completada' ? 'Completada' : 'Marcar como completada';
  };

  // DateRangePicker handlers
  const handleDateChange = (ranges: RangeKeyDict) => {
    const selection = ranges.selection;
    setDateRange({
      startDate: selection.startDate || new Date(),
      endDate: selection.endDate || new Date(),
      key: 'selection',
    });
  };

  const saveDateRange = async () => {
    if (
      dateRange.startDate?.toISOString().slice(0, 10) === task.startDate?.slice(0, 10) &&
      dateRange.endDate?.toISOString().slice(0, 10) === task.endDate?.slice(0, 10)
    ) {
      setShowDatePicker(false);
      return;
    }
    try {
      const updatedTask = {
        ...task,
        startDate: dateRange.startDate?.toISOString().slice(0, 10),
        endDate: dateRange.endDate?.toISOString().slice(0, 10),
      };
      await updateTaskHook({
        taskId: task.id,
        task: updatedTask
      }, {
        onSuccess: () => {
          refetchTasks();
          setShowDatePicker(false);
        }
      });
    } catch (error) {
      handleAxiosError(error, "Error al actualizar la fecha");
    }
  };

  const { user } = useAuth();
  const { firstname, firstlastname } = user?.data || {};
  const [isCommentEditorActive, setIsCommentEditorActive] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentTempFiles, setCommentTempFiles] = useState<File[]>([]);
  const [showFixedHeader, setShowFixedHeader] = useState(false);
  const { mutateAsync: createCommentHook, isPending: isCreatingComment } = useCreateComment();
  const { refetch: refetchComments } = useGetAllCommentsByTask(task.id);

  const createComment = async () => {
    try {
      const trimmedText = commentText.trim();
      if (!trimmedText) {
        toast.error("El comentario no puede estar vacío");
        return;
      }

      const formData: ICreateCommentFormData = {
        text: trimmedText,
        taskId: task.id || "",
        author: (firstname && firstlastname) ? `${firstname} ${firstlastname}` : "",
        createdAt: formatCreatedAt(new Date()),
      };

      const response = await createCommentHook(formData);
      
      // Si hay archivos temporales para subir, los subimos ahora con el ID del comentario
      if (commentTempFiles.length > 0) {
        await handleCommentFileUpload(commentTempFiles, response.data.id);
        setCommentTempFiles([]); // Limpiar archivos temporales
      }

      setCommentText("");
      setIsCommentEditorActive(false);
      refetchComments();
      toast.success("Comentario creado exitosamente");
    } catch (error) {
      console.error("Error al crear el comentario", error);
      toast.error("Error al crear el comentario");
    }
  };

  const cancelComment = () => {
    setCommentText("");
    setIsCommentEditorActive(false);
    setCommentTempFiles([]); // Limpiar archivos temporales
  };

  const handleProjectChange = async (projectId: string) => {
    try {
      setSelectedProjectId(projectId);
      
      // Actualizar las secciones y obtener la sección por defecto
      const defaultSection = await updateSections(projectId);
      
      // Actualizar la tarea con el nuevo proyecto y sección
      await updateTaskHook({
        taskId: task.id,
        task: { 
          ...task, 
          projectId,
          sectionId: String(defaultSection.id)
        }
      });

      // Actualizar el proyecto seleccionado
      const currentProject = projects.find((p: IProjectsData) => String(p.id) === projectId);
      if (currentProject) {
        setSelectedProject(currentProject);
      }

      toast.success("Proyecto actualizado exitosamente");
      if (onTaskUpdate) {
        onTaskUpdate();
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error("Error al actualizar el proyecto");
      }
    }
  };

  const handleSectionChange = async (sectionId: string) => {
    try {
      setSelectedSection(sections.find(s => String(s.id) === sectionId) || null);
      setValue('sectionId', sectionId);
      await trigger('sectionId');
      
      await updateTaskHook({
        taskId: task.id,
        task: { ...task, sectionId }
      });
      
      toast.success('Sección actualizada exitosamente');
      onTaskUpdate?.();
    } catch (error) {
      handleAxiosError(error, "Error al actualizar la sección");
    }
  };

  // Funciones para manejar archivos adjuntos
  const handleFileUpload = async (files: File[]) => {
    try {
      for (const file of files) {
        await uploadFile({ file, taskId: task.id });
      }
      await refetchTaskFiles();
      
      toast.success(`${files.length} archivo(s) procesado(s) exitosamente`);
      
    } catch (error) {
      console.error('Error al cargar archivos:', error);
      toast.error('Error al cargar archivos - Problema de CORS detectado');
    }
  };

  const handleFileRemove = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
    toast.success('Archivo eliminado exitosamente');
  };

  const handleFileDownload = async (file: { id: string; fileName: string; fileType: string }) => {
    try {
      const response = await downloadDoc(file.id);

      if (!response || !response.data) {
        throw new Error('No se recibieron datos del documento');
      }

      const contentType = response.headers['content-type'];
      const blob = new Blob([response.data], { type: contentType });
      const url = URL.createObjectURL(blob);

      // Extraer el nombre del archivo del header o usar el nombre original
      const fileName = response.headers['content-disposition']
        ?.split('filename=')[1]
        ?.replace(/['"]/g, '')
        || file.fileName;

      // Crear enlace de descarga
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpiar la URL después de un tiempo
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);

      toast.success('Archivo descargado exitosamente');
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error('Error al descargar el archivo. Inténtalo nuevamente.');
    }
  };

  const handleFilePreview = async (file: File | { id: string; fileName: string; fileType: string }) => {
    try {
      let url: string;
      let contentType: string;

      // Si es un archivo del backend
      if ('id' in file && file.id) {
        const response = await downloadDoc(file.id);

        if (!response || !response.data) {
          throw new Error('No se recibieron datos del documento');
        }

        contentType = response.headers['content-type'];
        const blob = new Blob([response.data], { type: contentType });
        url = URL.createObjectURL(blob);
      } else if (file instanceof File) {
        // Si es un archivo temporal (File object)
        url = URL.createObjectURL(file);
        contentType = file.type;
      } else {
        throw new Error('Tipo de archivo no soportado');
      }

      // Abrir modal de previsualización
      setPreviewModal({
        isOpen: true,
        file,
        url,
        contentType
      });
    } catch (error) {
      console.error('Error al abrir vista previa:', error);
      toast.error('Error al abrir la vista previa del archivo.');
    }
  };

  const closePreviewModal = () => {
    if (previewModal.url) {
      URL.revokeObjectURL(previewModal.url);
    }
    setPreviewModal({
      isOpen: false,
      file: null,
      url: null,
      contentType: null
    });
  };

  const copyTaskLinkToClipboard = async () => {
    try {
      const taskUrl = `${window.location.origin}/projects/${idproject}/task/${task.id}`;
      await navigator.clipboard.writeText(taskUrl);
      toast.success('Enlace de la tarea copiado al portapapeles');
    } catch (error) {
      console.error('Error al copiar el enlace:', error);
      toast.error('Error al copiar el enlace');
    }
  };

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };

  const toggleDropdown = (fileId: string) => {
    setDropdownOpen(prev => ({
      isOpen: prev.fileId === fileId ? !prev.isOpen : true,
      fileId: prev.fileId === fileId ? null : fileId
    }));
  };

  const closeDropdown = () => {
    setDropdownOpen({
      isOpen: false,
      fileId: null
    });
  };

  // Funciones helper para acceder a propiedades de archivo de manera segura
  const getFileName = (file: File | { id: string; fileName: string; fileType: string }) => {
    return 'fileName' in file ? file.fileName : file.name;
  };



  const getFileSize = (file: File | { id: string; fileName: string; fileType: string }) => {
    return 'size' in file ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : 'Archivo guardado';
  };

  const isBackendFile = (file: File | { id: string; fileName: string; fileType: string }): file is { id: string; fileName: string; fileType: string } => {
    return 'id' in file;
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return 'file-pdf';
    if (ext === 'jpg' || ext === 'jpeg' || ext === 'png' || ext === 'gif' || ext === 'bmp' || ext === 'svg' || ext === 'webp') return 'file-image';
    if (ext === 'doc' || ext === 'docx') return 'file-text';
    if (ext === 'txt' || ext === 'rtf' || ext === 'md') return 'file-text';
    if (ext === 'zip' || ext === 'rar' || ext === '7z' || ext === 'tar' || ext === 'gz' || ext === 'bz2') return 'file-archive';
    if (ext === 'mp4' || ext === 'mov' || ext === 'avi' || ext === 'mkv' || ext === 'wmv' || ext === 'flv' || ext === 'webm') return 'file-video';
    if (ext === 'mp3' || ext === 'wav' || ext === 'flac' || ext === 'aac' || ext === 'ogg' || ext === 'wma') return 'file-audio';
    if (ext === 'xls' || ext === 'xlsx' || ext === 'csv') return 'file-spreadsheet';
    if (ext === 'ppt' || ext === 'pptx') return 'file-spreadsheet';
    if (ext === 'js' || ext === 'ts' || ext === 'jsx' || ext === 'tsx' || ext === 'html' || ext === 'css' || ext === 'scss' || ext === 'json' || ext === 'xml' || ext === 'php' || ext === 'py' || ext === 'java' || ext === 'cpp' || ext === 'c' || ext === 'cs' || ext === 'rb' || ext === 'go' || ext === 'rs' || ext === 'swift' || ext === 'kt') return 'file-code';
    if (ext === 'exe' || ext === 'msi' || ext === 'dmg' || ext === 'pkg' || ext === 'deb' || ext === 'rpm' || ext === 'apk') return 'file-generic';
    return 'file-generic'; // Icono por defecto
  };

  const getSimplifiedFileType = (fileType: string) => {
    // Simplificar tipos de archivo extensos
    const typeMap: { [key: string]: string } = {
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
      'application/vnd.ms-excel': 'XLS',
      'application/vnd.ms-powerpoint': 'PPT',
      'application/msword': 'DOC',
      'application/pdf': 'PDF',
      'image/jpeg': 'JPG',
      'image/png': 'PNG',
      'image/gif': 'GIF',
      'image/svg+xml': 'SVG',
      'image/webp': 'WEBP',
      'video/mp4': 'MP4',
      'video/avi': 'AVI',
      'video/mov': 'MOV',
      'video/webm': 'WEBM',
      'audio/mpeg': 'MP3',
      'audio/wav': 'WAV',
      'audio/ogg': 'OGG',
      'text/plain': 'TXT',
      'text/markdown': 'MD',
      'application/json': 'JSON',
      'application/xml': 'XML',
      'application/zip': 'ZIP',
      'application/x-rar-compressed': 'RAR',
      'text/html': 'HTML',
      'text/css': 'CSS',
      'application/javascript': 'JS',
      'application/typescript': 'TS',
      'application/x-php': 'PHP',
      'text/x-python': 'PY',
      'text/x-java-source': 'JAVA',
      'text/x-c++src': 'CPP',
      'text/x-csrc': 'C',
      'application/x-ruby': 'RB',
      'text/x-go': 'GO',
      'text/x-rust': 'RS',
      'text/x-swift': 'SWIFT',
      'text/x-kotlin': 'KT'
    };

    // Si encontramos una coincidencia exacta, la usamos
    if (typeMap[fileType.toLowerCase()]) {
      return typeMap[fileType.toLowerCase()];
    }

    // Si no, intentamos extraer la extensión del tipo MIME
    const mimeToExt: { [key: string]: string } = {
      'application/vnd.openxmlformats-officedocument': 'DOCX',
      'application/vnd.ms-': 'MS',
      'image/': 'IMG',
      'video/': 'VID',
      'audio/': 'AUD',
      'text/': 'TXT',
      'application/': 'APP'
    };

    for (const [prefix, ext] of Object.entries(mimeToExt)) {
      if (fileType.toLowerCase().startsWith(prefix)) {
        return ext;
      }
    }

    // Si todo falla, devolvemos las primeras letras del tipo
    return fileType.split('/')[1]?.toUpperCase().substring(0, 4) || 'FILE';
  };

  const isPreviewableFile = (fileType: string): boolean => {
    const previewableTypes = [
      // Imágenes
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/svg+xml', 'image/webp',
      // PDFs
      'application/pdf',
      // Videos
      'video/mp4', 'video/avi', 'video/mov', 'video/webm', 'video/mkv', 'video/wmv', 'video/flv',
      // Audio
      'audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/flac', 'audio/aac',
      // Texto
      'text/plain', 'text/markdown', 'text/html', 'text/css', 'text/javascript',
      'application/javascript', 'application/typescript', 'application/json', 'application/xml',
      // Código
      'text/x-python', 'text/x-java-source', 'text/x-c++src', 'text/x-csrc',
      'application/x-php', 'application/x-ruby', 'text/x-go', 'text/x-rust',
      'text/x-swift', 'text/x-kotlin'
    ];
    
    return previewableTypes.includes(fileType.toLowerCase());
  };

  const handleChipClick = (file: File | { id: string; fileName: string; fileType: string }, e: React.MouseEvent) => {
    // Prevenir que el clic se propague al dropdown
    e.stopPropagation();
    
    // Obtener el tipo de archivo
    const fileType = 'fileType' in file ? file.fileType : file.type;
    
    // Si es previsualizable, abrir vista previa
    if (isPreviewableFile(fileType)) {
      handleFilePreview(file);
    } else {
      // Si no es previsualizable, descargar directamente
      if ('id' in file) {
        handleFileDownload(file);
      } else {
        // Para archivos temporales, mostrar mensaje
        toast.info('Este tipo de archivo no se puede previsualizar. Se descargará automáticamente.');
      }
    }
  };

  // Funciones para manejar adjuntos en comentarios
  const handleCommentFileUpload = async (files: File[], commentId: string) => {
    setIsUploadingCommentDoc(true);
    try {
      await Promise.all(
        files.map(async (file) => {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("commentId", commentId);

          const response = await uploadCommentDoc(formData);
          if (response?.success) {
            toast.success("Adjunto subido correctamente");
          }
        })
      );
      refetchCommentDocs();
    } catch (error) {
      console.error("Error al subir el documento:", error);
      toast.error('Error al subir el documento. Inténtalo nuevamente.');
    } finally {
      setIsUploadingCommentDoc(false);
    }
  };

  const handleCommentFileDownload = async (fileId: string) => {
    try {
      const response = await downloadCommentDoc(fileId);

      if (!response?.data) {
        toast.error('No se recibieron datos del documento');
        throw new Error('No se recibieron datos del documento');
      }

      const contentType = response.headers['content-type'];
      const blob = new Blob([response.data], { type: contentType });
      const url = URL.createObjectURL(blob);

      const fileName = response.headers['content-disposition']
        ?.split('filename=')[1]
        ?.replace(/['"]/g, '')
        || `document-${fileId}`;

      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error('Error al descargar el documento. Inténtalo nuevamente.');
    }
  };

  const handleCommentTempFileRemove = (index: number) => {
    setCommentTempFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Detectar scroll para mostrar/ocultar header fijo
  useEffect(() => {
    const handleScroll = () => {
      const scrollableContent = document.querySelector('.asana-layout__scrollable-content');
      const titleSection = document.querySelector('.asana-layout__title-section');
      
      if (scrollableContent && titleSection) {
        const scrollTop = scrollableContent.scrollTop;
        const titleRect = titleSection.getBoundingClientRect();
        const containerRect = scrollableContent.getBoundingClientRect();
        
        // Calcular si el título está fuera de vista
        const titleBottom = titleRect.bottom;
        const containerTop = containerRect.top;
        const isTitleHidden = titleBottom < containerTop + 80; // 80px de margen
        
        console.log('Scroll Debug:', {
          scrollTop,
          titleBottom,
          containerTop,
          isTitleHidden
        });
        
        setShowFixedHeader(isTitleHidden);
      }
    };

    // Ejecutar una vez al montar para establecer el estado inicial
    handleScroll();

    const scrollableContent = document.querySelector('.asana-layout__scrollable-content');
    if (scrollableContent) {
      scrollableContent.addEventListener('scroll', handleScroll);
      return () => scrollableContent.removeEventListener('scroll', handleScroll);
    }
  }, []);

  return (
    <div className={`${showDetailProjectStyles["showdetailproject"]} ${isFullScreen ? showDetailProjectStyles["showdetailproject--fullscreen"] : ''}`}>
      <div className={showDetailProjectStyles["asana-layout"]}>
        {/* Header fijo que aparece al hacer scroll */}
        {showFixedHeader && (
          <div className={showDetailProjectStyles["asana-layout__fixed-header"]}>
            <div className={showDetailProjectStyles["asana-layout__fixed-header-left"]}>
              <div className={showDetailProjectStyles["asana-layout__fixed-task-info"]}>
                <span className={showDetailProjectStyles["asana-layout__fixed-task-title"]}>
                  {taskName || 'Sin título'}
                </span>
              </div>
              <button 
                className={`${showDetailProjectStyles["asana-layout__fixed-status-btn"]} ${
                  taskState === 'completada' ? showDetailProjectStyles["asana-layout__fixed-status-btn--completed"] : ''
                }`}
                onClick={changeTaskStatus}
                disabled={isUpdatingTask}
                title={taskState === 'completada' ? 'Completada' : 'Marcar como completa'}
              >
                <UIcon name="check" size={16} />
              </button>
            </div>
            
            <div className={showDetailProjectStyles["asana-layout__fixed-header-actions"]}>
              <button className={showDetailProjectStyles["asana-layout__action-btn"]}>
                <UIcon name="thumbs-up" size={16} />
              </button>
              <button className={showDetailProjectStyles["asana-layout__action-btn"]}>
                <UIcon name="attachment" size={16} />
              </button>
              <button 
                className={showDetailProjectStyles["asana-layout__action-btn"]}
                onClick={copyTaskLinkToClipboard}
                title="Copiar enlace de la tarea"
              >
                <UIcon name="copy" size={16} />
              </button>
              <button 
                className={showDetailProjectStyles["asana-layout__action-btn"]}
                onClick={toggleFullScreen}
                title={isFullScreen ? "Salir de pantalla completa" : "Ver en pantalla completa"}
              >
                <UIcon name="external-link" size={16} />
              </button>
              <button className={showDetailProjectStyles["asana-layout__action-btn"]}>
                <UIcon name="more" size={16} />
              </button>
              <button 
                className={showDetailProjectStyles["asana-layout__close-btn"]}
                onClick={closeDetailProject}
              >
                <UIcon name="cross" size={18} />
              </button>
            </div>
          </div>
        )}

        {/* Header original */}
        <div className={showDetailProjectStyles["asana-layout__header"]}>
          <div className={showDetailProjectStyles["asana-layout__header-left"]}>
            <button 
              className={`${showDetailProjectStyles["asana-layout__status-btn"]} ${
                taskState === 'completada' ? showDetailProjectStyles["asana-layout__status-btn--completed"] : ''
              }`}
              onClick={changeTaskStatus}
              disabled={isUpdatingTask}
            >
              <img src={checkIcon} alt="check" />
              {getStatusText()}
            </button>
          </div>
          
          <div className={showDetailProjectStyles["asana-layout__header-actions"]}>
            <button className={showDetailProjectStyles["asana-layout__action-btn"]}>
              <UIcon name="thumbs-up" size={16} />
            </button>
            <button className={showDetailProjectStyles["asana-layout__action-btn"]}>
              <UIcon name="attachment" size={16} />
            </button>
            <button 
              className={showDetailProjectStyles["asana-layout__action-btn"]}
              onClick={copyTaskLinkToClipboard}
              title="Copiar enlace de la tarea"
            >
              <UIcon name="copy" size={16} />
            </button>
            <button 
              className={showDetailProjectStyles["asana-layout__action-btn"]}
              onClick={toggleFullScreen}
              title={isFullScreen ? "Salir de pantalla completa" : "Ver en pantalla completa"}
            >
              <UIcon name="external-link" size={16} />
            </button>
            <button className={showDetailProjectStyles["asana-layout__action-btn"]}>
              <UIcon name="more" size={16} />
            </button>
            <button 
              className={showDetailProjectStyles["asana-layout__close-btn"]}
              onClick={closeDetailProject}
            >
              <UIcon name="cross" size={18} />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className={showDetailProjectStyles["asana-layout__content"]}>
          {/* Left Sidebar */}
          <div className={showDetailProjectStyles["asana-layout__sidebar"]}>
            {/* Project */}
            <div className={showDetailProjectStyles["asana-layout__field"]}>
              <label className={showDetailProjectStyles["asana-layout__field-label"]}>
                Proyecto
              </label>
              <div className={showDetailProjectStyles["asana-layout__field-content"]}>
                <ProjectsSelect
                  name="projectId"
                  setValue={setValue}
                  trigger={trigger}
                  list={projects}
                  setProjectList={setProjects}
                  originalList={projects}
                  placeHolder="Seleccionar proyecto"
                  onProjectChange={handleProjectChange}
                  selectedItem={selectedProject}
                />
              </div>
            </div>

            {/* Section */}
            <div className={showDetailProjectStyles["asana-layout__field"]}>
              <label className={showDetailProjectStyles["asana-layout__field-label"]}>
                Sección
              </label>
              <div className={showDetailProjectStyles["asana-layout__field-content"]}>
                <SectionsSelect
                  name="sectionId"
                  setValue={setValue}
                  trigger={trigger}
                  list={sections}
                  setProjectList={handleSetSections}
                  originalList={sections}
                  placeHolder="Seleccionar sección"
                  onSectionChange={handleSectionChange}
                  selectedItem={selectedSection}
                />
              </div>
            </div>

            {/* Assignee */}
            <div className={showDetailProjectStyles["asana-layout__field"]}>
              <label className={showDetailProjectStyles["asana-layout__field-label"]}>
                Assignee
              </label>
              <div className={showDetailProjectStyles["asana-layout__field-content"]}>
                <UserAssigneeSelector
                  users={userOptions}
                  assignedUser={assignedUser}
                  onAssign={handleAssign}
                  onUnassign={handleUnassign}
                  isLoading={isLoadingUsers}
                  placeholder="Buscar usuario..."
                />
              </div>
            </div>

            {/* Due Date (DateRangePicker) */}
            <div className={showDetailProjectStyles["asana-layout__field"]}>
              <label className={showDetailProjectStyles["asana-layout__field-label"]}>
                Fecha estimada
              </label>
              <div className={showDetailProjectStyles["asana-layout__field-content"]}>
                {!showDatePicker ? (
                  <div className={showDetailProjectStyles["asana-layout__date"]}>
                    <span className={showDetailProjectStyles["asana-layout__date-icon"]}>
                      <UIcon name="calendar" size={16} />
                    </span>
                    <span>{dateRange.startDate.toLocaleDateString('es-ES')} - {dateRange.endDate.toLocaleDateString('es-ES')}</span>
                    <button 
                      className={showDetailProjectStyles["asana-layout__date-remove"]} 
                      onClick={() => setShowDatePicker(true)}
                    >
                      Editar
                    </button>
                  </div>
                ) : (
                  <div className={`${showDetailProjectStyles["asana-layout__date-picker-wrapper"]} ${showDetailProjectStyles["custom-date-range-picker"]}`}>
                    <DateRangePicker
                      ranges={[dateRange]}
                      onChange={handleDateChange}
                      rangeColors={["var(--color-primary)"]}
                    />
                    <div className={showDetailProjectStyles["asana-layout__date-actions"]}>
                      <button 
                        className={`${showDetailProjectStyles["asana-layout__icon-btn"]} ${showDetailProjectStyles["asana-layout__icon-btn--primary"]}`} 
                        onClick={saveDateRange}
                        title="Guardar fecha"
                      >
                        <UIcon name="check" size={16} />
                      </button>
                      <button 
                        className={`${showDetailProjectStyles["asana-layout__icon-btn"]} ${showDetailProjectStyles["asana-layout__icon-btn--secondary"]}`} 
                        onClick={() => setShowDatePicker(false)}
                        title="Cancelar"
                      >
                        <UIcon name="cross" size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Projects */}
            <div className={showDetailProjectStyles["asana-layout__field"]}>
              <label className={showDetailProjectStyles["asana-layout__field-label"]}>
                Projects
              </label>
              <div className={showDetailProjectStyles["asana-layout__field-content"]}>
                <div className={showDetailProjectStyles["asana-layout__project"]}>
                  <div className={showDetailProjectStyles["asana-layout__project-icon"]}>
                    <img src={projectIcon} alt="project" />
                  </div>
                  <div className={showDetailProjectStyles["asana-layout__project-info"]}>
                    <span className={showDetailProjectStyles["asana-layout__project-name"]}>
                      {projectName}
                    </span>
                    <span className={showDetailProjectStyles["asana-layout__project-section"]}>
                      Backlog
                    </span>
                  </div>
                </div>
                <button className={showDetailProjectStyles["asana-layout__add-btn"]}>
                  Add to projects
                </button>
              </div>
            </div>

            {/* Dependencies */}
            <div className={showDetailProjectStyles["asana-layout__field"]}>
              <label className={showDetailProjectStyles["asana-layout__field-label"]}>
                Dependencies
              </label>
              <div className={showDetailProjectStyles["asana-layout__field-content"]}>
                <button className={showDetailProjectStyles["asana-layout__add-btn"]}>
                  Add dependencies
                </button>
              </div>
            </div>

            {/* Fields */}
            <div className={showDetailProjectStyles["asana-layout__field"]}>
              <label className={showDetailProjectStyles["asana-layout__field-label"]}>
                Fields
              </label>
              <div className={showDetailProjectStyles["asana-layout__field-content"]}>
                <div className={showDetailProjectStyles["asana-layout__custom-fields"]}>
                  <div className={showDetailProjectStyles["asana-layout__custom-field"]}>
                    <span><UIcon name="exclamation-triangle" size={14} /> Priority level?</span>
                    <span className={showDetailProjectStyles["asana-layout__priority-high"]}>High priority</span>
                  </div>
                  <div className={showDetailProjectStyles["asana-layout__custom-field"]}>
                    <span><UIcon name="zap" size={14} /> Effort level?</span>
                    <span className={showDetailProjectStyles["asana-layout__effort-high"]}>High effort</span>
                  </div>
                  <div className={showDetailProjectStyles["asana-layout__custom-field"]}>
                    <span><UIcon name="clipboard-list" size={14} /> Type (Work Requests - IT)</span>
                    <span className={showDetailProjectStyles["asana-layout__type-other"]}>Other</span>
                  </div>
                  <div className={showDetailProjectStyles["asana-layout__custom-field"]}>
                    <span><UIcon name="chart-bar" size={14} /> Task Progress</span>
                    <span className={showDetailProjectStyles["asana-layout__progress-done"]}>Done</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className={showDetailProjectStyles["asana-layout__main"]}>
            {/* Contenido scrolleable de la tarea */}
            <div className={showDetailProjectStyles["asana-layout__scrollable-content"]}>
              {/* Task Details Section */}
              <div className={showDetailProjectStyles["asana-layout__task-details"]}>
                {/* Title Section */}
                <div className={showDetailProjectStyles["asana-layout__title-section"]}>
                  <input
                    type="text"
                    value={taskName}
                    onChange={handleTaskNameChange}
                    onBlur={saveTaskName}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        saveTaskName();
                      }
                    }}
                    className={showDetailProjectStyles["asana-layout__title-input"]}
                    placeholder="Sin título"
                  />
                </div>

                {/* Description Section */}
                <div className={showDetailProjectStyles["asana-layout__description"]}>
                  <h3 className={showDetailProjectStyles["asana-layout__section-title"]}>
                    Descripción
                  </h3>
                  <div className={showDetailProjectStyles["asana-layout__description-content"]}>
                    {!isEditingDescription ? (
                      <div 
                        className={showDetailProjectStyles["asana-layout__description-preview"]}
                        onClick={startEditingDescription}
                        dangerouslySetInnerHTML={{ 
                          __html: task.description || '<em>Sin descripción</em>' 
                        }}
                      />
                    ) : (
                      <div className={showDetailProjectStyles["asana-layout__description-editor"]}>
                        <CKEditorShared
                          description={description}
                          onChangeDescription={onChangeDescription}
                          placeHolder="Agrega una descripción..."
                        />
                        <div className={showDetailProjectStyles["asana-layout__description-actions"]}>
                          <button 
                            onClick={saveTaskDescription}
                            disabled={isUpdatingTask}
                            className={showDetailProjectStyles["asana-layout__description-btn"]}
                          >
                            {isUpdatingTask ? 'Guardando...' : 'Guardar'}
                          </button>
                          <button 
                            onClick={cancelEditDescription}
                            className={showDetailProjectStyles["asana-layout__description-btn--secondary"]}
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Attachments Section */}
                <div className={showDetailProjectStyles["asana-layout__attachments"]}>
                  <h3 className={showDetailProjectStyles["asana-layout__section-title"]}>
                    Archivos adjuntos
                  </h3>
                  
                  {/* Grid de archivos existentes */}
                  <div className={showDetailProjectStyles["files-simple-container"]}>
                    {/* Archivos del backend */}
                    {taskFiles && taskFiles.map((file: { id: string; fileName: string; fileType: string; filePath: string }, index: number) => (
                      <div 
                        key={`backend-${index}`} 
                        className={showDetailProjectStyles["file-chip"]}
                        onClick={(e) => handleChipClick(file, e)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className={showDetailProjectStyles["file-chip-icon"]}>
                          <UIcon name={getFileIcon(file.fileName)} size={16} />
                        </div>
                        <div className={showDetailProjectStyles["file-chip-content"]}>
                          <div className={showDetailProjectStyles["file-chip-name"]} title={file.fileName}>
                            {file.fileName}
                          </div>
                          <div className={showDetailProjectStyles["file-chip-meta"]}>
                            {getSimplifiedFileType(file.fileType)} • Descargar
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => toggleDropdown(`backend-${index}`)}
                          className={showDetailProjectStyles["file-chip-dropdown-btn"]}
                          title="Más opciones"
                        >
                          <UIcon name="chevrons-up-down" size={12} />
                        </button>
                        
                        {/* Dropdown menu */}
                        {dropdownOpen.isOpen && dropdownOpen.fileId === `backend-${index}` && (
                          <div className={showDetailProjectStyles["file-dropdown-menu"]}>
                            <button
                              type="button"
                              onClick={() => {
                                handleFilePreview(file);
                                closeDropdown();
                              }}
                              className={showDetailProjectStyles["file-dropdown-item"]}
                            >
                              <UIcon name="eye" size={14} />
                              Vista previa
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                handleFileDownload(file);
                                closeDropdown();
                              }}
                              className={showDetailProjectStyles["file-dropdown-item"]}
                            >
                              <UIcon name="download" size={14} />
                              Descargar
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                    
                    {/* Archivos temporales (pendientes de subir) */}
                    {attachedFiles.map((file, index) => (
                      <div 
                        key={`temp-${index}`} 
                        className={`${showDetailProjectStyles["file-chip"]} ${showDetailProjectStyles["file-chip--temp"]}`}
                        onClick={(e) => handleChipClick(file, e)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className={showDetailProjectStyles["file-chip-icon"]}>
                          <UIcon name={getFileIcon(file.name)} size={16} />
                        </div>
                        <div className={showDetailProjectStyles["file-chip-content"]}>
                          <div className={showDetailProjectStyles["file-chip-name"]} title={file.name}>
                            {file.name}
                          </div>
                          <div className={showDetailProjectStyles["file-chip-meta"]}>
                            {getSimplifiedFileType(file.type)} • {(file.size / 1024 / 1024).toFixed(2)} MB - Pendiente
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => toggleDropdown(`temp-${index}`)}
                          className={showDetailProjectStyles["file-chip-dropdown-btn"]}
                          title="Más opciones"
                        >
                          <UIcon name="chevrons-up-down" size={12} />
                        </button>
                        
                        {/* Dropdown menu */}
                        {dropdownOpen.isOpen && dropdownOpen.fileId === `temp-${index}` && (
                          <div className={showDetailProjectStyles["file-dropdown-menu"]}>
                            <button
                              type="button"
                              onClick={() => {
                                handleFilePreview(file);
                                closeDropdown();
                              }}
                              className={showDetailProjectStyles["file-dropdown-item"]}
                            >
                              <UIcon name="eye" size={14} />
                              Vista previa
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                handleFileRemove(index);
                                closeDropdown();
                              }}
                              className={showDetailProjectStyles["file-dropdown-item"]}
                            >
                              <UIcon name="trash" size={14} />
                              Eliminar
                            </button>
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Área de carga de archivos simplificada */}
                    <div 
                      className={showDetailProjectStyles["file-upload-chip"]}
                      onClick={() => document.getElementById('file-upload-simple')?.click()}
                      title="Haz clic para agregar archivos"
                    >
                      <div className={showDetailProjectStyles["file-upload-chip-icon"]}>
                        <UIcon name="plus" size={16} />
                      </div>
                      <input
                        type="file"
                        multiple
                        onChange={(e) => {
                          const files = e.target.files;
                          if (files && files.length > 0) {
                            handleFileUpload(Array.from(files));
                          }
                        }}
                        style={{ display: 'none' }}
                        id="file-upload-simple"
                        accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.svg,.webp,.mp4,.mov,.avi,.mkv,.wmv,.flv,.webm,.mp3,.wav,.flac,.aac,.ogg,.wma,.zip,.rar,.7z,.tar,.gz,.bz2,.xls,.xlsx,.csv,.ppt,.pptx,.js,.ts,.jsx,.tsx,.html,.css,.scss,.json,.xml,.php,.py,.java,.cpp,.c,.cs,.rb,.go,.rs,.swift,.kt,.exe,.msi,.dmg,.pkg,.deb,.rpm,.apk"
                      />
                    </div>
                  </div>
                </div>

                {/* Comments List Section */}
                <div className={showDetailProjectStyles["asana-layout__comments-list"]}>
                  <h3 className={showDetailProjectStyles["asana-layout__section-title"]}>
                    Comentarios
                  </h3>
                  <TaskComments taskId={task.id} />
                </div>
              </div>
            </div>

            {/* Área de comentarios fija en la parte inferior */}
            <div className={showDetailProjectStyles["asana-layout__comment-input-fixed"]}>
              <div className={showDetailProjectStyles["asana-layout__comment-avatar"]}>
                <img 
                  src={`https://ui-avatars.com/api/?name=${firstname}+${firstlastname}&background=random`} 
                  alt="Avatar" 
                />
              </div>
              <div className={showDetailProjectStyles["asana-layout__comment-content"]}>
                {!isCommentEditorActive ? (
                  <div 
                    className={showDetailProjectStyles["asana-layout__comment-placeholder"]}
                    onClick={() => setIsCommentEditorActive(true)}
                  >
                    Escribe un comentario...
                  </div>
                ) : (
                  <div className={showDetailProjectStyles["asana-layout__comment-editor"]}>
                    <CKEditorShared
                      description={commentText}
                      onChangeDescription={setCommentText}
                      placeHolder="Escribe un comentario..."
                    />
                    
                    {/* Adjuntos del comentario */}
                    {commentDocs && commentDocs(task.id).length > 0 && (
                      <div className={showDetailProjectStyles["files-simple-container"]}>
                        {commentDocs(task.id).map((doc: IDocUploaded) => (
                          <div key={doc.id} className={showDetailProjectStyles["file-chip"]}>
                            <div className={showDetailProjectStyles["file-chip-icon"]}>
                              <UIcon name={getFileIcon(doc.fileName)} size={16} />
                            </div>
                            <div className={showDetailProjectStyles["file-chip-content"]}>
                              <div className={showDetailProjectStyles["file-chip-name"]} title={doc.fileName}>
                                {doc.fileName}
                              </div>
                              <div className={showDetailProjectStyles["file-chip-meta"]}>
                                {getSimplifiedFileType(doc.fileType)} • Descargar
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleCommentFileDownload(doc.id)}
                              className={showDetailProjectStyles["file-chip-dropdown-btn"]}
                              title="Descargar archivo"
                            >
                              <UIcon name="download" size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Archivos temporales del comentario */}
                    {commentTempFiles.length > 0 && (
                      <div className={showDetailProjectStyles["files-simple-container"]}>
                        {commentTempFiles.map((file, index) => (
                          <div key={`temp-${index}`} className={showDetailProjectStyles["file-chip"]}>
                            <div className={showDetailProjectStyles["file-chip-icon"]}>
                              <UIcon name={getFileIcon(file.name)} size={16} />
                            </div>
                            <div className={showDetailProjectStyles["file-chip-content"]}>
                              <div className={showDetailProjectStyles["file-chip-name"]} title={file.name}>
                                {file.name}
                              </div>
                              <div className={showDetailProjectStyles["file-chip-meta"]}>
                                {getSimplifiedFileType(file.type)} • {(file.size / 1024 / 1024).toFixed(2)} MB - Pendiente
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleCommentTempFileRemove(index)}
                              className={showDetailProjectStyles["file-chip-dropdown-btn"]}
                              title="Eliminar archivo"
                            >
                              <UIcon name="cross" size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div className={showDetailProjectStyles["asana-layout__comment-actions"]}>
                      {/* Botón de adjuntos */}
                      <button 
                        onClick={() => document.getElementById('comment-file-upload')?.click()}
                        disabled={isUploadingCommentDoc}
                        className={`${showDetailProjectStyles["asana-layout__comment-btn"]} ${showDetailProjectStyles["asana-layout__comment-btn--attachment"]}`}
                        title="Agregar adjunto"
                      >
                        <UIcon name="attachment" size={16} />
                      </button>
                      <input
                        type="file"
                        multiple
                        onChange={(e) => {
                          const files = e.target.files;
                          if (files && files.length > 0) {
                            setCommentTempFiles(Array.from(files)); // Almacenar archivos temporales
                          }
                        }}
                        style={{ display: 'none' }}
                        id="comment-file-upload"
                        accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.svg,.webp,.mp4,.mov,.avi,.mkv,.wmv,.flv,.webm,.mp3,.wav,.flac,.aac,.ogg,.wma,.zip,.rar,.7z,.tar,.gz,.bz2,.xls,.xlsx,.csv,.ppt,.pptx,.js,.ts,.jsx,.tsx,.html,.css,.scss,.json,.xml,.php,.py,.java,.cpp,.c,.cs,.rb,.go,.rs,.swift,.kt,.exe,.msi,.dmg,.pkg,.deb,.rpm,.apk"
                      />
                      
                      <button 
                        onClick={createComment}
                        disabled={isCreatingComment || !commentText.trim()}
                        className={`${showDetailProjectStyles["asana-layout__comment-btn"]} ${showDetailProjectStyles["asana-layout__comment-btn--primary"]}`}
                        title={isCreatingComment ? 'Enviando...' : 'Enviar comentario'}
                      >
                        {isCreatingComment ? (
                          <UIcon name="more" size={16} />
                        ) : (
                          <UIcon name="check" size={16} />
                        )}
                      </button>
                      <button 
                        onClick={cancelComment}
                        className={`${showDetailProjectStyles["asana-layout__comment-btn"]} ${showDetailProjectStyles["asana-layout__comment-btn--secondary"]}`}
                        title="Cancelar comentario"
                      >
                        <UIcon name="cross" size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Previsualización */}
      {previewModal.isOpen && previewModal.url && previewModal.file && (
        <div className={showDetailProjectStyles["preview-modal-overlay"]}>
          <div className={showDetailProjectStyles["preview-modal-content"]}>
            {/* Header del modal */}
            <div className={showDetailProjectStyles["preview-modal-header"]}>
              <div className={showDetailProjectStyles["preview-modal-file-info"]}>
                <UIcon 
                  name={getFileIcon(getFileName(previewModal.file))} 
                  size={24} 
                />
                <div className={showDetailProjectStyles["preview-modal-file-details"]}>
                  <span className={showDetailProjectStyles["preview-modal-file-name"]}>
                    {getFileName(previewModal.file)}
                  </span>
                  <span className={showDetailProjectStyles["preview-modal-file-meta"]}>
                    {getSimplifiedFileType(previewModal.contentType || '')} • {getFileSize(previewModal.file)}
                  </span>
                </div>
              </div>
              <div className={showDetailProjectStyles["preview-modal-actions"]}>
                {isBackendFile(previewModal.file) && previewModal.file && (
                  <button
                    onClick={() => handleFileDownload(previewModal.file as { id: string; fileName: string; fileType: string })}
                    className={showDetailProjectStyles["preview-modal-btn"]}
                    title="Descargar"
                  >
                    <UIcon name="download" size={16} />
                  </button>
                )}
                <button
                  onClick={closePreviewModal}
                  className={showDetailProjectStyles["preview-modal-btn"]}
                  title="Cerrar"
                >
                  <UIcon name="cross" size={16} />
                </button>
              </div>
            </div>

            {/* Contenido del archivo */}
            <div className={showDetailProjectStyles["preview-modal-body"]}>
              {previewModal.contentType?.startsWith('image/') ? (
                <img 
                  src={previewModal.url} 
                  alt={getFileName(previewModal.file)}
                  className={showDetailProjectStyles["preview-modal-image"]}
                />
              ) : previewModal.contentType?.startsWith('video/') ? (
                <video 
                  src={previewModal.url} 
                  controls
                  className={showDetailProjectStyles["preview-modal-video"]}
                >
                  Tu navegador no soporta el elemento de video.
                </video>
              ) : previewModal.contentType?.startsWith('audio/') ? (
                <audio 
                  src={previewModal.url} 
                  controls
                  className={showDetailProjectStyles["preview-modal-audio"]}
                >
                  Tu navegador no soporta el elemento de audio.
                </audio>
              ) : previewModal.contentType === 'application/pdf' ? (
                <iframe 
                  src={previewModal.url} 
                  className={showDetailProjectStyles["preview-modal-pdf"]}
                  title={getFileName(previewModal.file)}
                />
              ) : (
                <div className={showDetailProjectStyles["preview-modal-unsupported"]}>
                  <UIcon name="file-generic" size={64} />
                  <p>Vista previa no disponible para este tipo de archivo</p>
                  {isBackendFile(previewModal.file) && previewModal.file && (
                    <button
                      onClick={() => handleFileDownload(previewModal.file as { id: string; fileName: string; fileType: string })}
                      className={showDetailProjectStyles["preview-modal-download-btn"]}
                    >
                      <UIcon name="download" size={16} />
                      Descargar archivo
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
